import { inject, Injectable } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequestBuilder,
} from '@web/common';
import { SalesType } from '@web/shared/module/shop';
import { TableDataPayload, TableLazyLoad } from '@web/shared/ui';
import { map, Observable } from 'rxjs';
import { AdminOrderShipmentFormItem } from '../models/admin-order-shipment-form-item';
import { AdminOrderShipmentSelectableItemFilter } from '../models/admin-order-shipment-selectable-item-filter';
import { AdminOrderShipmentProductCollectionFilter } from '../models/admin-order-shipment-product-collection-filter';
import { AdminOrderShipmentProductCollectionItemResponse } from '../api/responses/admin-order-shipment-product-collection-item.response';

@Injectable({
  providedIn: 'root',
})
export class AdminOrderShipmentProductDataSourceService {
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly apiProvider = inject(ApiDependencyProviderService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminOrderShipmentSelectableItemFilter>,
  ): Observable<TableDataPayload<AdminOrderShipmentFormItem>> {
    const request = this.collectionRequestBuilder
      .page(lazyLoad.page)
      .pageSize(lazyLoad.rows)
      .sorts((builder) => builder.fromTableSort(lazyLoad.sort).buildAsArray())
      .filters((builder) =>
        builder
          .fromTableFilter(this.toProductFilter(lazyLoad))
          .appendText((builder) => builder.equalFor('type').build())
          .appendText((builder) => builder.caseInsensitiveLikeFor('name').build())
          .appendText((builder) =>
            builder.caseInsensitiveLikeFor('cosmeticBrandName').build(),
          )
          .buildAsArray(),
      )
      .build();
    const url = this.apiProvider.httpUrlFactory.withQueryParams(
      this.apiProvider.environment.ApiHostUrl.Shop,
      'admin/product',
      this.apiProvider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.apiProvider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.apiProvider.http
      .get(url, { context })
      .pipe(
        map(
          this.apiProvider.httpHelper.bodyToCollectionResponse(
            AdminOrderShipmentProductCollectionItemResponse,
          ),
        ),
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  new AdminOrderShipmentFormItem({
                    id: item.id,
                    name: item.name,
                    quantity: 1,
                    featuredFileAlt: item.featuredFile?.alt(),
                    featuredFileUrl: item.featuredFile?.url,
                    sourceName: item.cosmeticBrand?.name,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  private toProductFilter(
    lazyLoad: TableLazyLoad<AdminOrderShipmentSelectableItemFilter>,
  ): TableLazyLoad<AdminOrderShipmentProductCollectionFilter>['filter'] {
    return new TableLazyLoad<AdminOrderShipmentProductCollectionFilter>(
      lazyLoad.first,
      lazyLoad.rows,
      lazyLoad.sort,
      {
        name: lazyLoad.filter?.name,
        cosmeticBrandName: lazyLoad.filter?.sourceName,
        type: {
          value: SalesType.Simple,
        },
      },
    ).filter;
  }
}
